/**
 * LanguageTabsEditor - Reusable translation editor component
 *
 * Provides a language tab interface for editing multi-language content.
 * Matches the StringsTab.js UI pattern.
 *
 * Props:
 * - languages: Array of {code: 'en', name: 'English', nativeName: 'English'}
 * - defaultLang: Default language code (default: 'en')
 * - translations: Object keyed by locale { en: { field1: '', field2: '' }, de: {...} }
 * - fields: Array of field definitions [{ key: 'name', label: 'Name', type: 'input' | 'textarea', placeholder?: string, rows?: number }]
 * - onChange: (locale, field, value) => void
 * - compact: boolean - Use compact layout for inline usage
 */

import React, { useState } from 'react';
import { FiGlobe } from 'react-icons/fi';

const LanguageTabsEditor = ({
  languages = [],
  defaultLang = 'en',
  translations = {},
  fields = [],
  onChange,
  compact = false
}) => {
  const [selectedLang, setSelectedLang] = useState(defaultLang);

  // Ensure we have at least the default language
  const availableLanguages = languages.length > 0 ? languages : [
    { code: 'en', name: 'English', nativeName: 'English' }
  ];

  // Get value for a field in the selected language
  const getValue = (fieldKey) => {
    return translations[selectedLang]?.[fieldKey] || '';
  };

  // Get the English/default value for hint text
  const getDefaultValue = (fieldKey) => {
    return translations[defaultLang]?.[fieldKey] || '';
  };

  // Count non-default languages with translations
  const getTranslatedCount = () => {
    return Object.keys(translations).filter(locale => {
      if (locale === defaultLang) return false;
      const content = translations[locale];
      return content && Object.values(content).some(v => v && v.trim());
    }).length;
  };

  return (
    <div className={`border rounded-lg overflow-hidden ${compact ? '' : ''}`}>
      {/* Language Tabs */}
      <div className="flex items-center border-b bg-gray-50 px-2 py-1.5 gap-1 flex-wrap">
        <FiGlobe className="w-4 h-4 text-gray-400 mr-1" />
        {availableLanguages.map(lang => (
          <button
            key={lang.code}
            type="button"
            onClick={() => setSelectedLang(lang.code)}
            className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
              selectedLang === lang.code
                ? 'bg-white text-primary-700 shadow-sm font-medium'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            {lang.nativeName || lang.name}
            {lang.code === defaultLang && (
              <span className="ml-1 text-xs text-gray-400">(default)</span>
            )}
          </button>
        ))}
        {getTranslatedCount() > 0 && (
          <span className="ml-auto text-xs text-gray-400">
            {getTranslatedCount()} translation{getTranslatedCount() !== 1 ? 's' : ''}
          </span>
        )}
      </div>

      {/* Fields for selected language */}
      <div className={`p-4 space-y-4 ${compact ? 'p-3 space-y-3' : ''}`}>
        {fields.map(field => (
          <div key={field.key}>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {field.label}
              {/* Show English hint for non-default languages */}
              {selectedLang !== defaultLang && getDefaultValue(field.key) && (
                <span className="ml-2 text-xs text-gray-400 font-normal">
                  EN: "{getDefaultValue(field.key).length > 50
                    ? getDefaultValue(field.key).substring(0, 50) + '...'
                    : getDefaultValue(field.key)}"
                </span>
              )}
            </label>
            {field.type === 'textarea' ? (
              <textarea
                value={getValue(field.key)}
                onChange={(e) => onChange(selectedLang, field.key, e.target.value)}
                rows={field.rows || 3}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
                placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}...`}
              />
            ) : (
              <input
                type="text"
                value={getValue(field.key)}
                onChange={(e) => onChange(selectedLang, field.key, e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
                placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}...`}
              />
            )}
          </div>
        ))}

        {fields.length === 0 && (
          <p className="text-sm text-gray-400 italic">No translatable fields defined.</p>
        )}
      </div>
    </div>
  );
};

export default LanguageTabsEditor;
